import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import SpinWheel from "../../components/SpinWheel";

export default function CustomerSpin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [outlet, setOutlet] = useState(null);
  const [sessionId, setSessionId] = useState(localStorage.getItem(`rs_session_${slug}`) || null);
  const [spinning, setSpinning] = useState(false);
  const [targetIndex, setTargetIndex] = useState(null);
  const [pendingAttempt, setPendingAttempt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/outlet/${slug}`);
        setOutlet(data);
      } catch {
        setError("Outlet not found");
      }
    })();
  }, [slug]);

  // ensure we have a session
  useEffect(() => {
    if (!sessionId) {
      api.post(`/public/outlet/${slug}/scan`).then((r) => {
        const sid = r.data.session_id;
        localStorage.setItem(`rs_session_${slug}`, sid);
        setSessionId(sid);
      });
    }
  }, [sessionId, slug]);

  const startSpin = async () => {
    if (!sessionId || spinning) return;
    setSpinning(true);
    try {
      const screenshotUrl = localStorage.getItem(`rs_screenshot_${slug}`) || null;
      const { data } = await api.post(`/public/outlet/${slug}/spin`, {
        session_id: sessionId,
        screenshot_url: screenshotUrl,
      });
      if (data.already_spun) {
        // Navigate straight to reward
        const att = data.attempt;
        if (att.redemption_code) {
          navigate(`/r/${slug}/reward/${att.redemption_code}`, { replace: true });
        } else {
          // already lost
          navigate(`/r/${slug}/reward/no-win`, { replace: true, state: { attempt: att, outletName: outlet.name } });
        }
        return;
      }
      setPendingAttempt(data.attempt);
      setTargetIndex(data.segment_index);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Spin failed");
      setSpinning(false);
    }
  };

  const onWheelDone = () => {
    if (!pendingAttempt) return;
    if (pendingAttempt.reward_type !== "no_reward") {
      // confetti
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.5 }, scalar: 0.9 });
      setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { x: 0.2, y: 0.6 } }), 200);
      setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { x: 0.8, y: 0.6 } }), 400);
    }
    setTimeout(() => {
      if (pendingAttempt.redemption_code) {
        navigate(`/r/${slug}/reward/${pendingAttempt.redemption_code}`);
      } else {
        navigate(`/r/${slug}/reward/no-win`, { state: { attempt: pendingAttempt, outletName: outlet?.name } });
      }
    }, 1100);
  };

  if (error) return <div className="min-h-screen flex items-center justify-center">{error}</div>;
  if (!outlet) return <div className="min-h-screen flex items-center justify-center text-[var(--ink-muted)]">Loading…</div>;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-10" style={{ background: "var(--bg)" }}>
      <div className="absolute inset-0 dot-bg opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md text-center">
        <h1 className="display text-3xl sm:text-4xl font-black tracking-tighter">Spin to win!</h1>
        <p className="text-[var(--ink-muted)] mt-2 text-sm">Tap the button below to reveal your reward.</p>

        <div className="mt-8 flex justify-center">
          <SpinWheel
            segments={outlet.segments}
            spinning={spinning}
            targetIndex={targetIndex}
            onDone={onWheelDone}
            size={300}
          />
        </div>

        <button
          data-testid="customer-spin-btn"
          onClick={startSpin}
          disabled={spinning || !sessionId}
          className="btn-primary mt-10 mx-auto"
          style={{ padding: "16px 30px", fontSize: 16 }}
        >
          {spinning ? "Spinning…" : "SPIN!"}
        </button>
      </div>
    </div>
  );
}
